import { inject } from '@angular/core';
import { CanMatchFn, Route, Router, UrlSegment } from '@angular/router';
import { environment } from 'src/environments/environment';

/** Album categories that can render the new video detail page. Audio and recorded-video always use the classic page. */
export const VIDEO_DETAIL_V2_CATEGORIES = ['video'];
export const VIDEO_DETAIL_UI_PARAM = 'ui';
export const VIDEO_DETAIL_UI_STORAGE_KEY = 'bb_video_detail_ui';

/** Picks the page version. `?ui=classic` always wins so the old page is reachable as a backup. */
export function resolveVideoDetailVersion(tagCategory: string, uiChoice: string | null | undefined, flagEnabled: boolean): boolean {
  if (uiChoice === 'classic') return false;
  if (!VIDEO_DETAIL_V2_CATEGORIES.includes(tagCategory)) return false;
  if (uiChoice === 'new') return true;
  return !!flagEnabled;
}

/** Reads :tag_category from the URL using the position it has in the route's own path. */
export function tagCategoryFor(route: Route, segments: UrlSegment[]): string {
  const index = (route.path || '').split('/').indexOf(':tag_category');
  return index >= 0 && segments[index] ? segments[index].path : '';
}

/** `?ui=` on the URL, remembered for the browser tab so in-page navigations keep the same version. */
export function readUiChoice(queryValue: string | null | undefined, storage: Storage | null): string | null {
  try {
    if (queryValue === 'classic' || queryValue === 'new') {
      storage?.setItem(VIDEO_DETAIL_UI_STORAGE_KEY, queryValue);
      return queryValue;
    }
    return storage?.getItem(VIDEO_DETAIL_UI_STORAGE_KEY) || null;
  } catch {
    return queryValue || null;
  }
}

function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export const videoDetailVersionGuard: CanMatchFn = (route: Route, segments: UrlSegment[]) => {
  const navigation = inject(Router).getCurrentNavigation();
  const queryValue = navigation?.extractedUrl?.queryParams?.[VIDEO_DETAIL_UI_PARAM];
  const uiChoice = readUiChoice(queryValue, safeSessionStorage());
  return resolveVideoDetailVersion(tagCategoryFor(route, segments), uiChoice, environment.enableNewVideoDetailPage);
};
